import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
  AdminRoute,
  CenteredStatus,
  LoginScreen,
  PrivateRoute,
  SuperadminRoute,
  LegacySlotRedirect,
} from './auth';
import { DiagnosticsBridge, RouteBreadcrumbs } from './diagnostics';
import { AppChrome } from './components/AppChrome';
import { PublicFolderView } from './share';
import { useI18n } from './i18n';
import { PreferenceBridge } from './components/PreferenceBridge';
import { useDocumentTitle } from './lib/useDocumentTitle';

/*
 * DÉCOUPAGE DU BUNDLE (code-splitting).
 *
 * Restent CHARGÉS D'EMBLÉE les deux seules destinations d'un premier affichage :
 * l'écran de connexion et la page publique d'un dossier partagé. Tout le reste
 * est chargé à la demande — sans quoi un visiteur ANONYME de `/d/:slug`
 * téléchargeait aussi la console superadmin, l'explorateur et le moteur d'upload
 * résumable, qu'il n'a ni le droit ni l'occasion d'ouvrir.
 *
 * ⚠ Le service worker maison n'intercepte QUE les navigations (cf. `swFetchStrategy` :
 * les assets sont en `bypass`, servis par le cache HTTP du navigateur). Ces chunks
 * ne changent donc RIEN à la liste de l'app-shell — la PWA est intacte.
 */
const SpacesHome = lazy(() => import('./storage').then((m) => ({ default: m.SpacesHome })));
const StorageBrowser = lazy(() => import('./storage').then((m) => ({ default: m.StorageBrowser })));
const UploadScreen = lazy(() => import('./storage').then((m) => ({ default: m.UploadScreen })));
const TrashPage = lazy(() => import('./trash').then((m) => ({ default: m.TrashPage })));
const SuperadminConsole = lazy(() =>
  import('./admin').then((m) => ({ default: m.SuperadminConsole })),
);
const JoinView = lazy(() => import('./community').then((m) => ({ default: m.JoinView })));
const DepositView = lazy(() => import('./community').then((m) => ({ default: m.DepositView })));

/** Repli d'attente d'un chunk de route (annoncé aux lecteurs d'écran). */
function RouteFallback(): React.JSX.Element {
  const { t } = useI18n();
  return <CenteredStatus role="status">{t('app.loading')}</CenteredStatus>;
}

/**
 * Arbre de routes de la coquille T2 — « tout dans l'URL ».
 *
 * - `/login`     → écran de connexion (premier login ou ajout de compte).
 * - `/u/:slot/*` → pose le slot puis redirige vers l'URL propre (rétro-compat).
 * - `/d/:slug` → page PUBLIQUE de partage d'un DOSSIER (XFILE-06 ; ADR-0005),
 *   accessible DÉCONNECTÉ, donc HORS `PrivateRoute`/`AppChrome`. (`/f/:slug` d'un
 *   FICHIER est servi DIRECTEMENT par le serveur en octets bruts — intégration pnyx
 *   T12 ; ADR-0015 — il n'y a plus de page SPA à cette route.)
 * - Zone authentifiée (gardée par `PrivateRoute`) : `AppChrome` = layout partagé
 *   (en-tête persistant) au-dessus des écrans rendus dans `main` via `<Outlet>`.
 *     - `/`                 → ACCUEIL des 4 espaces (public/auto/api/user — XFILE-25).
 *     - `/folder/:folderId` → explorateur d'un dossier (deep-linkable, XFILE-11).
 *       ⚠ `/d/:…` est RÉSERVÉ au partage public (ci-dessus) — d'où `/folder/:…` ici.
 *     - `/upload`           → écran « Déposer » (upload rapide — XFILE-03). Ouvert à tout
 *       utilisateur authentifié : le droit de dépôt effectif est décidé par ESPACE côté
 *       serveur (`folderCapabilities`), plus par le rôle global.
 *     - `/trash`            → CORBEILLE (XFILE-44), `?before=` = curseur de page. RÉSERVÉE aux
 *       rôles `admin`/`superadmin` (XFILE-72) : c'est un outil d'exploitation, gardé par
 *       `AdminRoute` — à ne pas confondre avec `SuperadminRoute`, plus étroite.
 * - `*` → repli vers l'accueil.
 */
export function App(): React.JSX.Element {
  // Titre d'onglet piloté par le nom du site via `/api/health` (public) — correct
  // dès l'écran de login. Monté à la racine des routes pour couvrir tout l'arbre.
  useDocumentTitle();
  return (
    <>
      {/* Diagnostic (« Signaler un bug ») : publication du contexte + capture des
          navigations. Ne rendent rien, montés sous le routeur et l'auth. */}
      <DiagnosticsBridge />
      <RouteBreadcrumbs />
      {/* Hydrate langue/thème depuis le profil serveur au login (si aucune préférence
          locale). Ne rend rien. */}
      <PreferenceBridge />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/u/:slot/*" element={<LegacySlotRedirect />} />

          {/* Partage PUBLIC (non authentifié) : hors de la zone gardée. `/f/:slug`
              (fichier) est servi en octets bruts par le serveur (pnyx T12), pas ici. */}
          <Route path="/d/:slug" element={<PublicFolderView />} />

          {/* Accès communautaire PUBLIC (XFILE-08), hors `PrivateRoute`/`AppChrome` :
              `/join/:token` échange le jeton contre une session ; `/deposit/:token` est la
              page de dépôt seul (upload public → lien de partage). */}
          <Route path="/join/:token" element={<JoinView />} />
          <Route path="/deposit/:token" element={<DepositView />} />

          {/* Zone authentifiée : en-tête persistant (AppChrome) au-dessus de tout. */}
          <Route
            element={
              <PrivateRoute>
                <AppChrome />
              </PrivateRoute>
            }
          >
            <Route path="/" element={<SpacesHome />} />
            <Route path="/folder/:folderId" element={<StorageBrowser />} />
            <Route path="/upload" element={<UploadScreen />} />
            {/* CORBEILLE (XFILE-44) — lots de suppression restaurables 30 jours, RÉSERVÉE aux
                rôles `admin`/`superadmin` (XFILE-72).

                ⚠ RENVERSEMENT ASSUMÉ DE LA v2.5.0 (XFILE-53), qui l'avait ouverte à tout compte
                authentifié : le serveur filtrant déjà lot par lot sur `canManage`, un rôle `user`
                n'y voyait que ses propres suppressions, et l'enfermer privait d'un recours celui
                qui ratait les 8 secondes du toaster. Ce motif reste exact ; c'est la DÉCISION
                PRODUIT qui change. La corbeille énumère l'historique des suppressions du dépôt —
                chemins d'origine et auteurs compris — bien au-delà de ce que le travail d'un
                utilisateur l'amène à consulter : elle appartient à l'exploitation.

                `AdminRoute` et non `SuperadminRoute` : l'`admin` administre le dépôt sans avoir
                accès à la console. Cette garde n'est que la 2ᵉ ligne — le lien est masqué sur
                l'accueil et `GET /api/deletions` répond 403 —, elle ferme l'accès par URL.

                ⚠ `POST /api/deletions/:batch/restore` reste, elle, OUVERTE à tout authentifié :
                c'est le bouton « Annuler » du toaster, l'annulation immédiate d'un geste que
                l'utilisateur vient de faire. Ne pas « aligner » les deux. */}
            <Route
              path="/trash"
              element={
                <AdminRoute>
                  <TrashPage />
                </AdminRoute>
              }
            />
            {/* Console superadmin (XFILE-07/-09) : gardée en profondeur (rôle strict). */}
            <Route
              path="/admin"
              element={
                <SuperadminRoute>
                  <SuperadminConsole />
                </SuperadminRoute>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}
